import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useUserContext } from '../../contexts/UserContext';
import { useContentfulContext } from '../../contexts/ContentfulContext';
import { deleteSpecificAEMAdobeIO } from '../../utils/APIs';
import AgGridWrapper from '../UI/AgGrid';
import ConfirmModal from '../UI/Modal/confirmModal';
import PrimaryModal from '../UI/Modal/PrimaryModal';
import { PrimaryButton, ErrorButton } from "../UI";

const DeleteAEMAdobeIOModal = ({ setDeleteIntegration, id, setActionLoading, refreshAEMAdobeIOData }) => {
    const { userData } = useUserContext();
    const { content } = useContentfulContext();

    const [dependencies, setDependencies] = useState(null);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);
    const [showGrid, setShowGrid] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    useEffect(() => {
        const fetchDependencies = async () => {
            try {
                setLoading(true);
                const result = await deleteSpecificAEMAdobeIO(
                    userData.token,
                    userData.selectedOrganization.org_token,
                    id,
                    true // checkDependencies = true
                );
                if (result?.data && Object.keys(result.data).length > 0) {
                    setDependencies(result.data.data);
                    setMessage(result.data.message);
                }
            } catch (err) {
                setError(err?.message || 'Failed to check dependencies');
            } finally {
                setLoading(false);
            }
        };
        fetchDependencies();
    }, [id, userData]);

    const handleDelete = async () => {
        try {
            setActionLoading && setActionLoading(true);
            setIsDeleting(true);
            const result = await deleteSpecificAEMAdobeIO(
                userData.token,
                userData.selectedOrganization.org_token,
                id
            );
            if (result) {
                await refreshAEMAdobeIOData();
                setDeleteIntegration(false);
                toast.success(content.AEM_ADOBE_IO_CONTENT.delete_success_msg);
            }
        } catch (err) {
            setError(err?.message || 'Failed to delete integration');
        } finally {
            setActionLoading && setActionLoading(false);
            setIsDeleting(false);
            setShowConfirm(false);
        }
    };

    // Flatten deps for grid
    const rowData = useMemo(() => {
        const rows = [];
        if (dependencies?.experience_offer_mappings) {
            rows.push(
                ...dependencies.experience_offer_mappings.map(d => ({
                    type: 'Experience Mapping',
                    name: d.experience_name,
                    ...d,
                }))
            );
        }
        return rows;
    }, [dependencies]);

    const colDefs = [
        { headerName: 'Type', field: 'type', flex: 1 },
        { headerName: 'Name', field: 'name', flex: 2 },
        { headerName: 'ID', field: '_id', flex: 2 },
    ];

    return (
        <>
            <PrimaryModal
                title={"Review dependency"}
                hideModal={setDeleteIntegration}
            >
                <div className="p-6 space-y-6">
                    {loading ? (
                        <p className="text-center text-gray-500">{content.AEM_ADOBE_IO_CONTENT.delete_modal.checkingDependencies}</p>
                    ) : (
                        <>
                            {!showGrid && (
                                <>
                                    {message ? (
                                        <p className="text-base text-black mb-4">
                                            {message}
                                            <br />
                                            <span className="font-semibold text-red-500">
                                                {content.AEM_ADOBE_IO_CONTENT.delete_modal.warning}
                                            </span>
                                        </p>
                                    ) : (
                                        <p className="text-base text-black mb-4">
                                            {content.AEM_ADOBE_IO_CONTENT.delete_modal.description}
                                        </p>
                                    )}

                                    {error && <p className="text-red-500">{error}</p>}

                                    <div className="mb-4 flex justify-end  space-x-4">
                                        {message ? (
                                            <>
                                                <PrimaryButton handleClick={() => setShowGrid(true)}>
                                                {content.AEM_ADOBE_IO_CONTENT.delete_modal.reviewDependencies}
                                                </PrimaryButton>
                                                <ErrorButton handleClick={() => setShowConfirm(true)}>
                                                {content.AEM_ADOBE_IO_CONTENT.delete_modal.deleteIntegration}
                                                </ErrorButton>
                                            </>
                                        ) : (
                                            <ErrorButton loading={isDeleting} handleClick={handleDelete}>
                                                {content.AEM_ADOBE_IO_CONTENT.delete_modal.confirmText}
                                            </ErrorButton>
                                        )}
                                    </div>
                                </>
                            )}

                            {showGrid && (
                                <div>
                                    <p className="mb-2 font-semibold">{content.AEM_ADOBE_IO_CONTENT.delete_modal.dependenciesPreview}</p>
                                    <div style={{ height: "300px", overflow : "auto", width:"100%" }}>
                                        <AgGridWrapper
                                            tableId="aem-adobe-io-dependencies-table"
                                            rowData={rowData}
                                            columnDefs={colDefs}
                                        />
                                    </div>
                                    <div className="mt-4 flex justify-end  space-x-4">
                                        <PrimaryButton handleClick={() => setShowGrid(false)}>
                                            {content.AEM_ADOBE_IO_CONTENT.delete_modal.back}
                                        </PrimaryButton>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </PrimaryModal>

            {showConfirm && (
                <ConfirmModal
                    description={content.AEM_ADOBE_IO_CONTENT.delete_modal.description}
                    ConfirmText={content.AEM_ADOBE_IO_CONTENT.delete_modal.confirmText}
                    error={error}
                    title={content.AEM_ADOBE_IO_CONTENT.delete_modal.title}
                    loading={isDeleting}
                    hideModal={setShowConfirm}
                    handleConfirmation={confirmed => {
                        if (confirmed) handleDelete();
                        else setShowConfirm(false);
                    }}
                />
            )}
        </>
    );
};

export default DeleteAEMAdobeIOModal;
